import { LibraryNameInput } from '@components/LibraryNameInput/LibraryNameInput';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { ActionButton } from '@pages/ActionButton';
import { BaseModal } from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { Library } from '@stores/Library/Library';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useModalRegistration } from '@stores/ModalStore';
import { observer } from 'mobx-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { DEFAULT_LIBRARY_NAME, submitCreateLibrary } from './createLibraryFlow';
import { CustomWordHintsToggle } from './CustomWordHintsToggle';
import shell from './LibraryModalShell.module.scss';
import { MobileCreateLibraryDrawer } from './Mobile/MobileCreateLibraryDrawer';
import { SkipVisualIndexingToggle } from './SkipVisualIndexingToggle';

export type LibraryCreateModalOpenOptions = {
  /** Prefill the name field (e.g. the name already typed in the Add Media flow). */
  defaultName?: string;
  /**
   * When provided, the modal hands the newly-created library back to the caller
   * INSTEAD of navigating to it (and skips the "Library created" toast). Used by
   * the Add Media flow so the created library can be preselected as the upload
   * destination. When omitted, the modal navigates to the new library as before.
   */
  onCreated?: (library: Library) => void;
};

export type LibraryCreateModalRef = {
  open: (options?: LibraryCreateModalOpenOptions) => void;
  close: () => void;
};

// Moved to createLibraryFlow; re-exported here so existing importers keep working.
export { DEFAULT_LIBRARY_NAME };

export const LibraryCreateModal = observer(
  forwardRef<LibraryCreateModalRef, {}>((_, ref) => {
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState('');
    // "Skip visual indexing" → creates a transcript-only library. Off by default.
    const [skipVisuals, setSkipVisuals] = useState(false);
    // "Use custom word hints" → per-library transcription glossary (persisted on
    // create as settings.v1.glossary). Off + empty by default.
    const [useWordHints, setUseWordHints] = useState(false);
    const [wordHints, setWordHints] = useState<string[]>([]);
    // Optional "return to caller" callback (see LibraryCreateModalOpenOptions).
    const onCreatedRef = useRef<((library: Library) => void) | undefined>(undefined);
    const libraryManager = useLibraryManager();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    // Reuse the isMobile already in scope rather than a second resize subscription.
    const autoFocusOnDesktop = !isMobile;

    // Note: 'library-create' is intentionally shared with NewLibraryModalBase (and, on mobile,
    // with the MobileCreateLibraryDrawer this renders) so the drag-drop overlay is suppressed
    // while ANY library-creation surface is open. That sharing is why registration has to be
    // refcounted — see useModalRegistration.
    useModalRegistration('library-create', open);

    const onOpen = (options?: LibraryCreateModalOpenOptions) => {
      setNewName(options?.defaultName ?? '');
      setSkipVisuals(false);
      setUseWordHints(false);
      setWordHints([]);
      onCreatedRef.current = options?.onCreated;
      setOpen(true);
    };
    const onClose = () => {
      setNewName('');
      setSkipVisuals(false);
      setUseWordHints(false);
      setWordHints([]);
      onCreatedRef.current = undefined;
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    if (isMobile) {
      return (
        <MobileCreateLibraryDrawer
          open={open}
          onClose={onClose}
          onOpen={onOpen}
          defaultName={newName}
          onCreated={onCreatedRef.current}
        />
      );
    }

    return (
      <BaseModal
        open={open}
        onClose={onClose}
        className={shell.modal}
        title="Create new library"
        subtitle="A library groups related media so you can search it together."
      >
        <form
          className={shell.form}
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await submitCreateLibrary({
                libraryManager,
                navigate,
                name: newName,
                skipVisuals,
                wordHints: useWordHints ? wordHints : [],
                onCreated: onCreatedRef.current,
              });
              setNewName('');
              onClose();
            } catch (error) {
              console.error(error);
            }
          }}
        >
          <div className={shell.field}>
            <label htmlFor="library-name" className={shell.fieldLabel}>
              Name
            </label>
            <LibraryNameInput
              id="library-name"
              placeholder={DEFAULT_LIBRARY_NAME}
              value={newName}
              onValueChange={setNewName}
              autoFocus={autoFocusOnDesktop}
            />
          </div>
          <div className={shell.options}>
            <SkipVisualIndexingToggle checked={skipVisuals} onChange={setSkipVisuals} />
            <CustomWordHintsToggle
              checked={useWordHints}
              onChange={setUseWordHints}
              words={wordHints}
              onWordsChange={setWordHints}
            />
          </div>
          <div className={shell.actions}>
            <ActionButton variant="secondary" onClick={onClose} type="button">
              Cancel
            </ActionButton>
            <ActionButton type="submit">Create library</ActionButton>
          </div>
        </form>
      </BaseModal>
    );
  }),
);
